<template>
  <v-app :data-testid="getTestId('dashboard-app')">
    <CoreHeader :data-testid="getTestId('dashboard-header')" />
    <CoreLeftDrawer class="sidebar" :data-testid="getTestId('dashboard-sidebar')" />

    <v-main :data-testid="getTestId('dashboard-main')">
      <MainMapCard :data-testid="getTestId('dashboard-main-map')" />
    </v-main>

    <CoreFooter :data-testid="getTestId('dashboard-footer')" />

    <DefectListDrawer
      ref="defectDrawer"
      v-model="showDefectDrawer"
      :defects="dashboardDefects"
      :loading="defectsLoading || siteDesignLoading || wfsDefectRowsLoading"
      @select-defect="onDrawerDefectSelect"
      @create-on-module="onDrawerCreateOnModule"
      @fly-to-defect="onDrawerDefectFlyTo"
      @map-highlight="onDrawerMapHighlight"
    />

    <DefectInfoDialog :data-testid="getTestId('dashboard-defect-dialog')" />
    <AdminAlertBar :data-testid="getTestId('dashboard-alert-bar')" />
    <GlobalAlert />
  </v-app>
</template>

<script>
  import { mapActions, mapState, mapMutations } from 'vuex';
  import { ensureSession } from '@/auth';
  import { getTestId } from '@/utils/testUtils';
  import { appPerfMark, appPerfSinceMyProfile, appPerfScheduleFirstPaint, appPerfLazyChunk } from '@/utils/appBootstrapPerfLog.js';
  import DefectListDrawer from '@/features/defect-drawer/components/DefectListDrawer.vue';
  import {
    handleDashboardDefectSelect,
    handleDashboardDefectFlyTo,
    handleDashboardCreateOnModule,
    handleDashboardModulePencil
  } from '@/features/defect';
  import { logModuleListSync } from '@/features/defect-drawer/utils/moduleListSyncDebug';

  export default {
    components: {
      CoreHeader: appPerfLazyChunk('Dashboard.CoreHeader', () => import('../../components/core/CoreHeader')),
      CoreLeftDrawer: appPerfLazyChunk('Dashboard.CoreLeftDrawer', () => import('../../components/core/CoreLeftDrawer')),
      CoreFooter: appPerfLazyChunk('Dashboard.CoreFooter', () => import('../../components/core/CoreFooter')),
      MainMapCard: appPerfLazyChunk('Dashboard.MainMapCard', () => import('../../components/card/MainMapCard')),
      DefectInfoDialog: appPerfLazyChunk('Dashboard.DefectInfoDialog', () => import('../../components/dialog/DefectInfoDialog')),
      AdminAlertBar: appPerfLazyChunk('Dashboard.AdminAlertBar', () => import('../../components/snackbar/AdminAlertBar')),
      GlobalAlert: appPerfLazyChunk('Dashboard.GlobalAlert', () => import('../../components/common/GlobalAlert.vue')),
      DefectListDrawer
    },

    data: () => ({
      loadingApi: false,
      dashboardDefects: [],
      defectsLoading: false
    }),

    computed: {
      ...mapState('dashboard', {
        dashboardDefectsComputed: 'wfsDefectRows',
        siteDesignLoading: 'siteDesignLoading',
        wfsDefectRowsLoading: 'wfsDefectRowsLoading'
      }),
      ...mapState('explorer', {
        currentReportId: state => state.report?.id
      }),

      /** EventBus → Vuex: showDefectDrawer */
      showDefectDrawer: {
        get() { return this.$store.state.dashboard.defectDrawerOpen; },
        set(val) { this.setDefectDrawerOpen(val); }
      }
    },

    watch: {
      currentReportId(reportId) {
        if (reportId == null) {
          this.setDefectDrawerOpen(false);
          this.dashboardDefects = [];
        }
      },
      dashboardDefectsComputed: {
        handler(rows) { this.dashboardDefects = rows || []; },
        immediate: true
      },

      /** 지도 Module 클릭 → Drawer 행 스크롤 (양방향 동기화) */
      '$store.state.dashboard.moduleClickTarget'(target) {
        logModuleListSync('dashboard.moduleClickTarget', {
          target,
          drawerOpen: this.showDefectDrawer,
          hasDrawerRef: Boolean(this.$refs.defectDrawer),
          siteDesignLoading: this.siteDesignLoading,
          defectsLoading: this.defectsLoading,
          defectCount: this.dashboardDefects?.length ?? 0
        });
        if (!target?.solarModuleSeq) return;
        this.$nextTick(() => {
          this.$refs.defectDrawer?.scrollToModule(target.solarModuleSeq);
        });
      }
    },

    methods: {
      ...mapActions({
        resetAiDefectViewState: 'dashboard/resetAiDefectViewState'
      }),
      ...mapMutations('dashboard', [
        'setDefectDrawerOpen',
        'setDefectFlyTarget',
        'setMapHighlightTarget'
      ]),

      getTestId(testId) { return getTestId(testId); },

      async onDrawerDefectSelect(defect) {
        await handleDashboardDefectSelect(this, defect);
      },
      async onDrawerCreateOnModule(row) {
        await handleDashboardCreateOnModule(this, row);
      },
      async onMapModulePencil(payload) {
        await handleDashboardModulePencil(this, payload?.solarModuleSeq);
      },
      onDrawerDefectFlyTo(defect) {
        handleDashboardDefectFlyTo(this, defect);
      },
      onDrawerMapHighlight(payload) {
        const { row } = payload || {};
        if (!row?.kind) return;
        this.setMapHighlightTarget({
          kind: row.kind,
          seq: row.seq,
          displayId: row.displayId,
          arrayDisplayId: row.arrayDisplayId,
          stringDisplayId: row.stringDisplayId,
          arraySeq: payload.arraySeq ?? row.arraySeq ?? (row.kind === 'array_header' ? row.seq : null),
          stringSeq: payload.stringSeq ?? row.stringSeq ?? (row.kind === 'string_header' ? row.seq : null)
        });
      }
    },

    async mounted() {
      appPerfMark('DashboardPage:mounted');
      appPerfSinceMyProfile('DashboardPage:mounted');
      appPerfMark('DashboardPage:ensureSession:start');
      await ensureSession();
      appPerfMark('DashboardPage:ensureSession:done');
      appPerfMark('DashboardPage:resetAiDefect:start');
      await this.resetAiDefectViewState();
      appPerfMark('DashboardPage:resetAiDefect:done');
      appPerfMark('DashboardPage:getUserDashboardData:dispatch');
      this.$store.dispatch('dashboard/getUserDashboardData');
      appPerfSinceMyProfile('DashboardPage:dataDispatch');
      appPerfScheduleFirstPaint('DashboardPage');
      this.$eventBus.$on('dashboard-module-pencil', this.onMapModulePencil);
    },

    beforeDestroy() {
      this.$eventBus.$off('dashboard-module-pencil', this.onMapModulePencil);
      this.setMapHighlightTarget(null);
    },

    /** Error Boundary — 하위 컴포넌트 트리 오류 격리 */
    errorCaptured(err, vm, info) {
      console.error('[DashboardPage] errorCaptured:', err?.message, info);
      return false;
    }
  };
</script>

<style>
</style>
